/**
 * Client Document Hooks
 *
 * React hooks for client (customer) document operations.
 * Mirrors trade document hooks but scoped to customer ownership.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

type DocumentCategory = "TRADE_LICENSE" | "INSURANCE" | "SAFETY_CERTIFICATE" | "IDENTIFICATION" | "BUSINESS_DOCUMENT" | "OTHER";
type DocumentType = string;

/**
 * Fetch document requirements for client onboarding.
 * Queries document_requirements WHERE company_type = 'CLIENT'.
 */
export function useClientDocumentRequirements(businessState?: string | null) {
    return useQuery({
        queryKey: ["client-document-requirements", businessState],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("document_requirements")
                .select("*, document_type_definitions(display_name)")
                .eq("company_type", "CLIENT");

            if (error) throw error;

            // Filter by state: keep national requirements (state is null/empty)
            // and requirements matching the customer's business state
            return (data ?? []).filter(req => {
                if (!req.state) return true; // National requirement — always include
                if (!businessState) return false; // No state known — exclude state-specific
                return req.state === businessState;
            });
        },
        staleTime: 60_000, // 60 seconds — requirements don't change often
    });
}

/**
 * Fetch documents owned by a customer.
 */
export function useClientDocuments(customerId: string | null) {
    return useQuery({
        queryKey: ["client-documents", customerId],
        queryFn: async () => {
            if (!customerId) throw new Error("Customer ID is required");

            const { data, error } = await supabase
                .from("trade_documents")
                .select("*, document_type_definitions(display_name)")
                .eq("customer_id", customerId)
                .eq("is_current", true)
                .order("upload_timestamp", { ascending: false });

            if (error) throw error;
            return data;
        },
        enabled: !!customerId,
        staleTime: 10_000,
    });
}

/**
 * Upload a document for a client (customer).
 * Sets customer_id + owner_type = 'CUSTOMER' on the trade_documents row.
 */
export function useUploadClientDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: {
            customerId: string;
            file: File;
            documentType: DocumentType;
            category?: DocumentCategory;
            expiryDate?: string;
            metadata?: Record<string, unknown>;
        }) => {
            // 1. Upload file to storage — always use trade-documents bucket for clients
            // (avoids bucket-not-found errors for specialty buckets like insurance-documents)
            // Path must start with customerId so the storage RLS policy can match it.
            const bucketName = 'trade-documents';
            const fileExt = params.file.name.split(".").pop();
            const filePath = `${params.customerId}/documents/${Date.now()}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from(bucketName)
                .upload(filePath, params.file, {
                    cacheControl: "3600",
                    upsert: false,
                });

            if (uploadError) throw uploadError;

            // 2. Create document record with CUSTOMER ownership
            const { data, error } = await supabase
                .from("trade_documents")
                .insert(
                    {
                        customer_id: params.customerId,
                        trade_company_id: null,
                        profile_id: null,
                        owner_type: "CUSTOMER",
                        document_type: params.documentType,
                        compliance_category: params.category ?? null,
                        file_name: params.file.name,
                        file_path: filePath,
                        file_size: params.file.size,
                        mime_type: params.file.type,
                        expiry_date: params.expiryDate || null,
                        extracted_data: params.metadata
                            ? JSON.parse(JSON.stringify(params.metadata))
                            : null,
                        bucket_id: bucketName,
                    },
                )
                .select()
                .single();

            if (error) throw error;
            return data;
        },
        onSuccess: (_data, variables) => {
            // Invalidate client document queries
            queryClient.invalidateQueries({
                queryKey: ["client-documents", variables.customerId],
            });
            // Also invalidate onboarding status so document completeness re-checks
            queryClient.invalidateQueries({
                queryKey: ["clientOnboarding", "documents", variables.customerId],
            });
        },
    });
}

/**
 * Check document compliance status for a client.
 */
export function useClientDocumentCompliance(customerId: string | null, businessState?: string | null) {
    const { data: requirements } = useClientDocumentRequirements(businessState);
    const { data: documents } = useClientDocuments(customerId);

    return useQuery({
        queryKey: ["client-document-compliance", customerId, businessState, requirements, documents],
        queryFn: async () => {
            if (!requirements || !documents) {
                return {
                    totalRequired: 0,
                    uploaded: 0,
                    verified: 0,
                    missing: [],
                    expired: [],
                    expiringSoon: [],
                    pending: [],
                    completionPercentage: 0,
                };
            }

            // Deduplicate mandatory requirements by document_type
            const seenDocTypes = new Set();
            const mandatoryRequirements = requirements
                .filter((r) => r.is_mandatory)
                .filter((r) => {
                    if (seenDocTypes.has(r.document_type)) return false;
                    seenDocTypes.add(r.document_type);
                    return true;
                });

            const now = new Date();
            const thirtyDaysFromNow = new Date();
            thirtyDaysFromNow.setDate(now.getDate() + 30);

            const missing = mandatoryRequirements.filter((req) => {
                return !documents.some((doc) =>
                    doc.document_type === req.document_type &&
                    doc.verification_status !== "REJECTED"
                );
            });

            const expired = documents.filter((doc) => {
                if (!doc.expiry_date) return false;
                return new Date(doc.expiry_date) < now;
            });

            const expiringSoon = documents.filter((doc) => {
                if (!doc.expiry_date) return false;
                const expDate = new Date(doc.expiry_date);
                return expDate >= now && expDate <= thirtyDaysFromNow;
            });

            const pending = documents.filter((doc) =>
                doc.verification_status === "PENDING"
            );

            const verified = documents.filter((doc) =>
                doc.verification_status === "APPROVED"
                || doc.verification_status === 'VERIFIED'
            );

            const totalRequired = mandatoryRequirements.length;
            const completionPercentage = totalRequired > 0
                ? Math.round((verified.length / totalRequired) * 100)
                : 0;

            return {
                totalRequired,
                uploaded: documents.length,
                verified: verified.length,
                missing: missing.map((r) => r.document_type),
                expired: expired.map((d) => d.document_type as string),
                expiringSoon: expiringSoon.map((d) => d.document_type as string),
                pending: pending.map((d) => d.document_type as string),
                completionPercentage,
            };
        },
        enabled: !!customerId && !!requirements && !!documents,
    });
}
